import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AlertType, CartItem, Product } from '../types/common';
import { RootState } from './store';
import { showAlert } from './CommonSlice';
import { ADDED_TO_WISHLIST, REMOVED_FROM_WISHLIST } from '../constants/messages';
import { getCsrfToken } from '../utils/helpers';
import { clearSession } from './AuthSlice';
import { fetchProducts } from './ProductSlice';

type WishList = Product['id'][];

export type UserState = {
    wishlist: WishList;
    cart: CartItem[];
    bonusPoints?: number;
    birthdayEligible?: boolean;
};

const initialState: UserState = {
    wishlist: [],
    cart: [],
    bonusPoints: 0,
    birthdayEligible: false,
};

/* --------- helper для пересчёта  ---------- */
function resolveDiscount(raw: unknown, base: number): number | undefined {
    if (raw == null) return undefined;
    if (typeof raw === 'number') return Math.round(base * (1 - raw / 100));
    if (typeof raw === 'object') {
        const r: any = raw;
        if (typeof r.discountedPrice === 'number') return r.discountedPrice;
        if (typeof r.percent === 'number') return Math.round(base * (1 - r.percent / 100));
    }
    return undefined;
}
function effectivePrice(p: Product): number {
    return resolveDiscount(p.specialDiscount, p.price) ?? resolveDiscount(p.discount, p.price) ?? p.price;
}
function recalcCart(cart: CartItem[], products: Product[]) {
    return cart.map((item) => {
        const prod = products.find((p) => p.id === item.productId);
        if (!prod) return item;
        const base = prod.price;
        const eff = effectivePrice(prod);
        const qty = item.quantity;
        return {
            ...item,
            price: base,
            discountedPrice: eff === base ? undefined : eff,
            totalPrice: eff * qty,
            profit: (base - eff) * qty,
        };
    });
}

// === E-commerce helper для Яндекс.Метрики (add to cart) ===
const YM_ECOM_ID = Number(process.env.REACT_APP_YM_ID || 0);
function pushEcommerceAddToCart(item: CartItem) {
    if (process.env.NODE_ENV !== 'production') return;
    if (!YM_ECOM_ID) return;
    if (typeof window === 'undefined') return;
    const unitPriceSource = (item as any).discountedPrice ?? (item as any).specialDiscountedPrice ?? (item as any).price;
    const unitPrice = typeof unitPriceSource === 'number' && Number.isFinite(unitPriceSource) ? unitPriceSource : undefined;
    if (!unitPrice) return;
    const qty = Number.isFinite(Number(item.quantity)) && Number(item.quantity) > 0 ? Number(item.quantity) : 1;
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
        ecommerce: {
            currencyCode: 'RUB',
            add: {
                products: [
                    {
                        id: String(item.productId),
                        name: String(item.name || ''),
                        price: Number(unitPrice),
                        quantity: qty,
                    },
                ],
            },
        },
    });
}

export const setToLocalStorage = createAsyncThunk<void, string, { state: RootState }>('user/setToLS', async (key, { getState }) => {
    const {
        user: { wishlist, cart },
        auth: { isAuthenticated },
    } = getState();
    if (isAuthenticated) {
        // Если пользователь авторизован, очищаем localStorage для данного ключа
        localStorage.removeItem(key);
        // Для авторизованного пользователя обновляем данные напрямую в БД через API
        if (key === 'wishlist') {
            const csrfToken = await getCsrfToken();
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user/favorites`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'csrf-token': csrfToken,
                },
                credentials: 'include',
                body: JSON.stringify({ favorites: wishlist }),
            });
            if (!response.ok) {
                console.error('Ошибка обновления избранного:', await response.text());
            }
        }
        if (key === 'cart') {
            const csrfToken = await getCsrfToken();
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user/cart`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'csrf-token': csrfToken,
                },
                credentials: 'include',
                body: JSON.stringify({ cart }),
            });
            if (!response.ok) {
                console.error('Ошибка обновления корзины:', await response.text());
            }
        }
        return;
    } else {
        // Для неавторизованных пользователей сохраняем данные в localStorage
        if (key === 'wishlist') {
            localStorage.setItem(key, JSON.stringify(wishlist));
        }
        if (key === 'cart') {
            localStorage.setItem(key, JSON.stringify(cart));
        }
    }
});

export const getFromLocalStorage = createAsyncThunk<void, string, { state: RootState }>('user/setWishlistToLS', async (key, { dispatch, getState }) => {
    const {
        auth: { isAuthenticated },
    } = getState();
    if (isAuthenticated) {
        // Если пользователь авторизован, получаем данные из БД через API
        if (key === 'wishlist') {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user/favorites`, {
                method: 'GET',
                credentials: 'include',
            });
            if (response.status === 401) {
                dispatch(clearSession());
                return;
            }
            const data = await response.json();
            dispatch(setWishlist(data.favorites));
        }
        if (key === 'cart') {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user/cart`, {
                method: 'GET',
                credentials: 'include',
            });
            if (response.status === 401) {
                dispatch(clearSession());
                return;
            }
            const data = await response.json();
            dispatch(setProductsToCart(data.cart));
        }
        return;
    }
    if (key === 'wishlist') {
        const wishlist = JSON.parse(localStorage.getItem(key) as any);
        if (!wishlist) return;
        dispatch(setWishlist(wishlist));
    }
    if (key === 'cart') {
        const cart = JSON.parse(localStorage.getItem(key) as any);
        if (!cart) return;
        dispatch(setProductsToCart(cart));
    }
});

export const wishListHandler = createAsyncThunk<void, { id: string; isWished: boolean }, { state: RootState }>(
    'user/wishListHandler',
    async ({ id, isWished }, { dispatch, getState }) => {
        dispatch(handleWishlist(id));
        const {
            auth: { isAuthenticated },
        } = getState();
        if (isAuthenticated) {
            // для авторизованного пользователя вызов setToLocalStorage обновит данные в БД
            await dispatch(setToLocalStorage('wishlist'));
        } else {
            dispatch(setToLocalStorage('wishlist'));
        }
        if (isWished) {
            dispatch(showAlert({ type: AlertType.Info, message: REMOVED_FROM_WISHLIST }));
        } else {
            dispatch(showAlert({ type: AlertType.Success, message: ADDED_TO_WISHLIST, action: 'wishlist' }));
        }
    },
);

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setWishlist: (state, action: PayloadAction<unknown>) => {
            const next = Array.isArray(action.payload) ? action.payload : [];
            state.wishlist = next.filter((x): x is string => typeof x === 'string' && x.length > 0);
        },
        handleWishlist: (state, action: PayloadAction<Product['id']>) => {
            const isProductLiked = state.wishlist.find((wish) => wish === action.payload);
            if (isProductLiked) {
                const updatedWishlist = state.wishlist.filter((wish) => wish !== action.payload);
                state.wishlist = updatedWishlist;
                return;
            }
            state.wishlist.push(action.payload);
        },
        setProductsToCart: (state, action: PayloadAction<CartItem[]>) => {
            const safe = Array.isArray(action.payload) ? action.payload : [];
            state.cart = safe
                .map((it) => {
                    const pid = String((it as any)?.productId ?? '').trim();
                    if (!pid) return null;
                    const qty = Number.isFinite(Number(it.quantity)) && Number(it.quantity) > 0 ? Math.floor(Number(it.quantity)) : 1;
                    const wt = Number.isFinite(Number(it.weight)) && Number(it.weight) > 0 ? Number(it.weight) : 0;
                    return {
                        productId: pid,
                        name: it.name ?? '',
                        quantity: qty,
                        weight: wt,
                        totalWeight: wt * qty,
                    } as CartItem;
                })
                .filter(Boolean) as CartItem[];
        },

        setProductToCart: (state, action: PayloadAction<CartItem>) => {
            const pid = String((action.payload as any)?.productId ?? '').trim();
            if (!pid) return;

            const qty = Number.isFinite(Number(action.payload.quantity)) && Number(action.payload.quantity) > 0 ? Math.floor(Number(action.payload.quantity)) : 1;
            const wt = Number.isFinite(Number(action.payload.weight)) && Number(action.payload.weight) > 0 ? Number(action.payload.weight) : 0;

            const existing = state.cart.find((c) => c.productId === pid);
            if (existing) {
                existing.quantity += qty;
                existing.totalWeight += wt * qty;
            } else {
                state.cart.push({
                    productId: pid,
                    name: action.payload.name ?? '',
                    quantity: qty,
                    weight: wt,
                    totalWeight: wt * qty,
                } as CartItem);
            }

            // E-commerce: добавление товара в корзину
            try {
                const ecomItem = {
                    ...(action.payload as any),
                    productId: pid,
                    name: action.payload.name ?? '',
                    quantity: qty,
                    weight: wt,
                    totalWeight: wt * qty,
                } as CartItem;
                pushEcommerceAddToCart(ecomItem);
            } catch {
                // ignore
            }
        },
        increment: (state, action: PayloadAction<CartItem['productId']>) => {
            const item = state.cart.find((c) => c.productId === action.payload);
            if (!item) return;
            const wt = Number.isFinite(Number(item.weight)) ? Number(item.weight) : 0;
            item.quantity += 1;
            item.totalWeight += wt;
        },

        decrement: (state, action: PayloadAction<CartItem['productId']>) => {
            const item = state.cart.find((c) => c.productId === action.payload);
            if (!item || item.quantity === 1) return;
            const wt = Number.isFinite(Number(item.weight)) ? Number(item.weight) : 0;
            item.quantity -= 1;
            item.totalWeight -= wt;
        },
        removeProductFromCart: (state, action: PayloadAction<CartItem['productId']>) => {
            const updatedCart = state.cart.filter((cartItem) => cartItem.productId !== action.payload);
            state.cart = updatedCart;
        },
        clearCart: (state) => {
            state.cart = initialState.cart;
        },
        setBonus: (state, action: PayloadAction<{ points: number }>) => {
            state.bonusPoints = action.payload.points;
        },
        setDiscount: (state, action: PayloadAction<{ birthdayEligible: boolean }>) => {
            state.birthdayEligible = action.payload.birthdayEligible;
        },
    },
    // extraReducers: (builder) => {
    //     builder.addCase(fetchProducts.fulfilled, (state, { payload }) => {
    //         state.cart = recalcCart(state.cart, payload);
    //     });
    // },
    extraReducers: (builder) => {
        builder.addCase(clearSession, () => initialState);
        builder.addCase(fetchProducts.fulfilled, (state, { payload }) => {
            state.cart = recalcCart(state.cart, payload);
        });
    },
});

export const { handleWishlist, setWishlist, setProductToCart, increment, decrement, removeProductFromCart, clearCart, setProductsToCart, setBonus, setDiscount } =
    userSlice.actions;

export default userSlice.reducer;
